import React, { useContext, useMemo, useState } from 'react';
import AuthenticationContext from '../contexts/auth/AuthenticationContext';
import { useCompanyCredits } from '../hooks/plans/useCompanyCredits';
import { useLeedbucksConfig } from '../hooks/plans/useLeedbucksConfig';
import { useCallsStats } from '../hooks/calls/useCallsStats';
import PageScaffold from '../components/PageScaffold';
import {
  StatCard,
  QuickActionsCard,
  NotificationsCard,
  LeaderboardCard,
  PlanUsageCard,
  UnderstandPlansModal,
  CallsSmsMonthlyStatCard,
} from '../components/overview_page';
import { colors } from '../../domain/types/colors';
import {
  FileTextOutlined,
  PhoneOutlined,
  TeamOutlined,
  UserAddOutlined,
} from '@ant-design/icons';
import CompanyContext from '../contexts/company/CompanyContext';
import { formatDate } from '../helpers';
import { ContextStatus, UserCompanyType } from '@leedpulse-monorepo/enums';
import ActivityChart from './statistics/components/ActivityChart';
import LoadingCurtain from '../components/@shared/LoadingCurtain';
import { useNavigate } from 'react-router-dom';
import { USERS_PAGE_PATH } from './users/UsersPage';
import { SUBSCRIPTIONS_PAGE_PATH } from './subscriptions/SubscriptionsPage';
import { COMPANY_BILLING_PAGE_PATH } from './company/CompanyBillingPage';

import './OverviewPage.css';

export const OVERVIEW_PAGE_PATH = '/overview';
export const OVERVIEW_PAGE_TITLE = 'Tableau de bord';

interface OverviewPageProps { }

const OverviewPage: React.FC<OverviewPageProps> = () => {
  const { userDoc, getUserCompanyRole } = useContext(AuthenticationContext);
  const { activeCompany, usersCount, usersStatus, hasValidPayment } = useContext(CompanyContext);
  const { config: leedbucksConfig, isLoading: ratesLoading, isError: ratesUnavailable } = useLeedbucksConfig({ allSubscriptions: true }); // summed number by number
  const { creditsUsage, companyPlans, isLoading: creditsLoading } = useCompanyCredits(leedbucksConfig);
  const { callsStats, loading: callsLoading } = useCallsStats();

  const usersLoading = usersStatus === ContextStatus.LOADING || usersStatus === ContextStatus.IDLE;
  const [understandOpen, setUnderstandOpen] = useState(false);
  const navigate = useNavigate();

  // On a monthly plan the credits cycle IS the billing period, so the two dates are the same day
  // and printing both just repeats. Only an annual plan pulls them apart.
  const nextBillingDate = useMemo<string | undefined>(() => {
    const iPeriodEnd = activeCompany?.billing?.period_end;
    if (!iPeriodEnd) return undefined;

    const sFormatted = formatDate(iPeriodEnd);
    return sFormatted === creditsUsage.nextResetDate ? undefined : sFormatted;
  }, [activeCompany?.billing?.period_end, creditsUsage.nextResetDate]);

  const isSupervisor = useMemo<boolean | undefined>(() => {
    if (!activeCompany?.id) return undefined;
    return getUserCompanyRole(activeCompany.id) === UserCompanyType.SUPERVISOR;
  }, [activeCompany?.id, getUserCompanyRole]);

  const quickActions = [
    {
      id: 'invite-user',
      icon: <UserAddOutlined />,
      label: 'Inviter un utilisateur',
      onClick: () => navigate(USERS_PAGE_PATH, { state: { openModal: 'ADD_USER' } }),
    },
    {
      id: 'order-number',
      icon: <PhoneOutlined />,
      label: 'Commander un numéro',
      onClick: () => navigate(SUBSCRIPTIONS_PAGE_PATH, { state: { openModal: 'ORDER_NUMBER' } }),
    },
    {
      id: 'view-invoice',
      icon: <FileTextOutlined />,
      label: 'Voir mes factures',
      onClick: () => navigate(COMPANY_BILLING_PAGE_PATH),
    },
  ];

  // Don't show anything before we know the user's status
  if (isSupervisor === undefined) {
    return (
      <PageScaffold title="" subtitle="" breadcrumbItems={[]}>
        <LoadingCurtain />
      </PageScaffold>
    );
  }

  return (
    <PageScaffold title="" subtitle="" breadcrumbItems={[]}>
      <div className="overview-page">
        {/* Header */}
        <div className="overview-page__greeting">
          <span className="overview-page__wave" role="img" aria-label="Salut">
            👋
          </span>
          <h1 className="overview-page__title">
            Bonjour {userDoc?.firstname} {userDoc?.lastname}
          </h1>
        </div>
        {isSupervisor === true ?
          <>
            {/* Stats Cards Row */}
            <div className="overview-page__stats-row">
              <StatCard
                icon={<TeamOutlined style={{ fontSize: 24, color: colors.primary.main }} />}
                label="UTILISATEURS"
                value={String(usersCount)}
                subtitle="utilisateurs"
                iconBgColor="green"
                loading={usersLoading}
              />
              <PlanUsageCard
                usage={creditsUsage}
                onUnderstand={() => setUnderstandOpen(true)}
                loading={creditsLoading || ratesLoading}
                ratesUnavailable={ratesUnavailable}
                nextBillingDate={nextBillingDate}
              />
              <CallsSmsMonthlyStatCard
                callsValue={callsStats.totalCalls}
                smsValue={callsStats.smsSentCount}
                loading={callsLoading}
              />


              {/* 
                <StatCard
                  icon={<ThunderboltOutlined style={{ fontSize: 24, color: colors.primary.main }} />}
                  label="ACTIVITÉ"
                  value="85%"
                  subtitle="Des agents actifs ce mois"
                  iconBgColor="green"
                  badge={{ value: '+14%', type: 'positive' }}
                  comingSoon
                /> 
              */}
            </div>

            {/* Activity Chart */}
            <ActivityChart
              icon={<TeamOutlined style={{ fontSize: 24, color: colors.primary.main }} />}
              label="ACTIVITÉ"
              iconBgColor="green"
              dailyStats={callsStats.dailyStats}
              status={callsLoading ? ContextStatus.LOADING : ContextStatus.SUCCESS}
            />

            {/* Action Cards Row */}
            {isSupervisor ?
              (<div className="overview-page__action-row">
                <QuickActionsCard actions={quickActions} />
                <NotificationsCard notifications={[]} comingSoon />
                <LeaderboardCard users={[]} comingSoon />
              </div>)
              : null
            }

          </> : null
        }
      </div>
      <UnderstandPlansModal open={understandOpen} onClose={() => setUnderstandOpen(false)} plans={companyPlans} />
    </PageScaffold>
  );
};

export default OverviewPage;
